import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

const MENU_WIDTH = 160;

export default function RowActionsMenu({ label, actions, className = 'row-actions-trigger', children, disabled = false }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [position, setPosition] = useState(null);
  const isOpen = Boolean(position);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutside = event => {
      if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) setPosition(null);
    };
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setPosition(null);
        triggerRef.current?.focus();
      }
    };
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const height = Math.min(actions.length * 36 + 12, window.innerHeight - 16);
      const tableLeft = triggerRef.current.closest('table')?.getBoundingClientRect().left;
      const minimumLeft = Math.max(8, tableLeft ?? 8);
      const preferredLeft = children ? rect.right - MENU_WIDTH : rect.left;
      const left = Math.min(Math.max(minimumLeft, preferredLeft), window.innerWidth - MENU_WIDTH - 8);
      const top = rect.bottom + height + 6 <= window.innerHeight - 8 ? rect.bottom + 6 : Math.max(8, rect.top - height - 6);
      setPosition({ top, left });
    };
    updatePosition();
    menuRef.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen, actions.length, children]);

  return <>
    <button
      ref={triggerRef}
      type="button"
      className={className}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={Boolean(position)}
      onClick={event => {
        event.stopPropagation();
        if (position) setPosition(null);
        else {
          const rect = event.currentTarget.getBoundingClientRect();
          setPosition({ top: rect.bottom + 6, left: Math.min(Math.max(8, children ? rect.right - MENU_WIDTH : rect.left), window.innerWidth - MENU_WIDTH - 8) });
        }
      }}
    >{children || <MoreHorizontal size={17} />}</button>
    {position && <div ref={menuRef} className="wh-action-menu row-actions-menu" role="menu" style={position}>
      {actions.map(({ label: actionLabel, icon: Icon, onClick, disabled, danger }) => <button
        key={actionLabel}
        type="button"
        role="menuitem"
        className={`wh-action-item${danger ? ' danger' : ''}`}
        disabled={disabled}
        onClick={() => {
          setPosition(null);
          onClick?.(triggerRef.current);
        }}
      ><Icon size={14} aria-hidden="true" />{actionLabel}</button>)}
    </div>}
  </>;
}
